// --- Core ---
import {ElementType, ReactNode} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {any, first} from "underscore";
// --- Material UI ---
import {Link} from "@mui/material";
// --- SaniQ ---
import {useDialogToggle} from "helpers/Dialog";
import {firstLine, isEmpty, isMultiline} from "helpers/StringUtil";

interface TruncateProps {
  /**
   * Component to render the text in expanded or truncated form. The text is passed via the children prop.
   * @default Typography
   */
  component?: ElementType;
  displayText?: ReactNode;
  /**
   * Maximum number of lines that are shown without truncation (as long as none are longer than truncateChars)
   * @default 3
   */
  maxLines?: number;
  /**
   * Text to be truncated
   */
  text?: string;
  /**
   * Number of characters after which the truncation happens
   * @default 50
   */
  truncateChars?: number;
  lsKey?: string;
}

/**
 * Should the text be truncated according to the given configuration?
 * Also gives the truncated text, when too much content is given
 */
const truncate = (text: string, truncateChars: number, moreTextLength: number, maxLines: number): [boolean, string] => {
  //NOTE(Keune): Take the added text into consideration, to not make the text longer by shortening it
  const maxLineLength = truncateChars + moreTextLength;

  if (isMultiline(text)) {
    const firstLines = firstLine(text, maxLines + 1).split("\n");

    if (firstLines.length <= maxLines) {
      return [any(first(firstLines, maxLines), e => e.length > maxLineLength), firstLines[0].substring(0, truncateChars)];
    } else {
      return [true, firstLines[0].substring(0, truncateChars)];
    }
  } else if (text.length > maxLineLength) {
    return [true, text.substring(0, truncateChars)];
  } else {
    return [false, text];
  }
};

/**
 * Show a text in a truncated version with a button to switch between the truncated
 * and full version.
 */
const Truncate = ({displayText, maxLines = 3, text, truncateChars = 50, lsKey = undefined}: TruncateProps) => {
  const intl = useIntl();
  const [truncateOpened, _open, _close, handleTruncateToggle] = useDialogToggle(false, lsKey);
  const moreText = intl.formatMessage({id: "truncate.more", defaultMessage: "... more"});

  if (isEmpty(text)) {
    return null;
  }
  const [hasMore, truncatedText] = truncate(text, truncateChars, moreText.length, maxLines);

  if (hasMore) {
    return (
      <>
        {truncateOpened ? (displayText || text) : truncatedText}
        <Link underline={"none"} component={"button"} type={"button"} onClick={handleTruncateToggle}>
          {truncateOpened ? <FormattedMessage id="truncate.less" defaultMessage="less"/> : moreText}
        </Link>
      </>
    );
  } else {
    return <>{displayText || text}</>;
  }
};

export {
  Truncate,
  TruncateProps
};
